import {
    useSyncExternalStore,
    useImperativeHandle,
    useLayoutEffect,
    createContext,
    forwardRef,
    useContext,
    useEffect,
    useState,
    type FC,
    useRef,
    useId
} from "react";
import {
    Animated,
    Platform,
    View
} from "react-native";
import type IModalProps from "./type";
import type {
    IModalLayerProps,
    ModalLayerType,
    IModalRef
} from "./type";
import stylesheet, {
    MODAL_EDITABLE_TAG_NAMES,
    MODAL_TAB_OWNER_DATA_KEY,
    MODAL_FORWARDED_KEYS,
    getModalPortalName,
    getModalCloseStyle,
    getModalMotion,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import type {
    RefForwardingComponent,
    NCoreUIKitIcon
} from "../../types";
import {
    MOTION_SURFACE_SCALE,
    useReducedMotion
} from "../../helpers/motion";
import {
    useOverlayAccessibility
} from "../../helpers/overlay";
import {
    usePointerType
} from "../../helpers/pointer";
import {
    X as XIcon
} from "lucide-react-native";
import {
    SURFACE_CONTAINER_COLOR
} from "../../helpers/theme/elevation";
import {
    warnOnce
} from "../../utils/development";
import {
    Portal
} from "../../helpers/portalize";
import {
    uuid
} from "../../utils";
import AnimatedIcon from "../animatedIcon";
import Button from "../button";

const ModalCloseIcon: NCoreUIKitIcon = ({
    customColor,
    color,
    size
}) => <AnimatedIcon
    customColor={customColor}
    animationTrigger="hover"
    animation="signature"
    color={color}
    icon={XIcon}
    size={size}
/>;

const ModalPortalContext = createContext<string | undefined>(undefined);

const modalLayers: Array<ModalLayerType> = [];

const modalLayerListeners = new Set<() => void>();

const getScrimOwnerKey = () => modalLayers.filter((layer) => layer.isScrim).pop()?.key;

const getStackPortalName = () => modalLayers.filter((layer) => layer.portalName !== undefined).pop()?.portalName;

const getIsScrimRegistered = (key: string) => modalLayers.some((layer) => layer.isScrim && layer.key === key);

const getIsScrimResponder = () => true;

const getIsEditableTarget = (target: EventTarget | null) => {
    if(typeof HTMLElement === "undefined" || !(target instanceof HTMLElement)) {
        return false;
    }

    return target.isContentEditable || MODAL_EDITABLE_TAG_NAMES.includes(target.tagName);
};

const getIsTabOwnerTarget = (target: EventTarget | null) => {
    if(typeof HTMLElement === "undefined" || !(target instanceof HTMLElement)) {
        return false;
    }

    return target.dataset[MODAL_TAB_OWNER_DATA_KEY] === "true";
};

const subscribeModalLayers = (listener: () => void) => {
    modalLayerListeners.add(listener);

    return () => {
        modalLayerListeners.delete(listener);
    };
};

const notifyModalLayers = () => {
    modalLayerListeners.forEach((listener) => {
        listener();
    });
};

const addModalLayer = (layer: ModalLayerType) => {
    modalLayers.push(layer);

    notifyModalLayers();
};

const removeModalLayer = (key: string) => {
    const layerIndex = modalLayers.map((layer) => layer.key).lastIndexOf(key);

    if(layerIndex === -1) {
        return;
    }

    modalLayers.splice(layerIndex, 1);

    notifyModalLayers();
};

const ModalLayer: FC<IModalLayerProps> = ({
    "aria-describedby": ariaDescribedBy,
    "aria-labelledby": ariaLabelledBy,
    "aria-label": ariaLabel,
    accessibilityLabel,
    isDisabledOverlay,
    onContainerLayout,
    isOverlayVisible,
    initialFocusRef,
    customLocalize,
    returnFocusRef,
    isShowSurface,
    alignContent,
    contentStyle,
    onClosePress,
    overlayProps,
    customTheme,
    isShowClose,
    opacityAnim,
    portalName,
    onDismiss,
    scaleAnim,
    children,
    isActive,
    motion,
    style,
    role
}) => {
    const {
        controlHeights,
        shapeConfigs,
        touchTargets,
        shadowAlpha,
        elevations,
        typography,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const layerKey = useId();

    const scrimOwnerKey = useSyncExternalStore(subscribeModalLayers, getScrimOwnerKey, getScrimOwnerKey);

    const containerRef = useRef<View>(null);

    const scrimOpacityAnim = useRef(new Animated.Value(1)).current;

    const isScrimRequested = isOverlayVisible && isActive;

    const isScrimOwner = isScrimRequested && (scrimOwnerKey === layerKey || !getIsScrimRegistered(layerKey));

    const isNativeDriver = Platform.OS !== "web";

    const {
        containerProps
    } = useOverlayAccessibility({
        isDisabledOverlay,
        initialFocusRef,
        returnFocusRef,
        containerRef,
        onDismiss,
        isActive
    });

    const closeStyle = getModalCloseStyle({
        controlHeights,
        touchTargets,
        pointerType,
        typography,
        iconSizes,
        radiuses,
        borders,
        spaces
    });

    const {
        surface: surfaceDynamicStyle,
        dialog: dialogDynamicStyle,
        gutter: gutterDynamicStyle,
        scrim: scrimDynamicStyle,
        close: closeDynamicStyle
    } = useStyles({
        alignContent,
        shapeConfigs,
        shadowAlpha,
        closeStyle,
        elevations,
        radiuses,
        borders,
        colors,
        spaces
    });

    useLayoutEffect(() => {
        if(!isActive) {
            return;
        }

        addModalLayer({
            isScrim: isOverlayVisible,
            key: layerKey,
            portalName
        });

        return () => {
            removeModalLayer(layerKey);
        };
    }, [
        isOverlayVisible,
        portalName,
        layerKey,
        isActive
    ]);

    useEffect(() => {
        if(!isActive) {
            return;
        }

        Animated.timing(scrimOpacityAnim, {
            duration: isScrimOwner ? motion.closeFadeDuration : motion.openFadeDuration,
            easing: isScrimOwner ? motion.closeEasing : motion.openEasing,
            useNativeDriver: isNativeDriver,
            toValue: isScrimOwner ? 1 : 0
        }).start();
    }, [
        isScrimOwner
    ]);

    useEffect(() => {
        return () => {
            scrimOpacityAnim.stopAnimation();
        };
    }, []);

    useEffect(() => {
        const containerNode: unknown = containerRef.current;

        if(!isActive || typeof HTMLElement === "undefined" || !(containerNode instanceof HTMLElement)) {
            return;
        }

        const onEditableKeyDown = (event: KeyboardEvent) => {
            if(event.defaultPrevented || event.isComposing || !MODAL_FORWARDED_KEYS.includes(event.key) || !getIsEditableTarget(event.target) || getIsTabOwnerTarget(event.target)) {
                return;
            }

            const forwardedEvent = new KeyboardEvent("keydown", {
                shiftKey: event.shiftKey,
                cancelable: true,
                key: event.key,
                bubbles: true
            });

            containerNode.dispatchEvent(forwardedEvent);

            if(forwardedEvent.defaultPrevented) {
                event.preventDefault();
            }
        };

        containerNode.addEventListener("keydown", onEditableKeyDown, true);

        return () => {
            containerNode.removeEventListener("keydown", onEditableKeyDown, true);
        };
    }, [
        isActive
    ]);

    const webProps = Platform.OS === "web" ? {
        "aria-describedby": ariaDescribedBy
    } : {};

    const webScrimProps = Platform.OS === "web" ? {
        dataSet: {
            disablePressableHoverEffect: "true",
            disablePressableDownEffect: "true"
        }
    } : {};

    const onScrimRelease = () => {
        if(onDismiss) onDismiss();
    };

    const renderScrim = () => {
        if(!isOverlayVisible) {
            return null;
        }

        return <Animated.View
            {...overlayProps}
            {...webScrimProps}
            style={[
                overlayProps?.style,
                stylesheet.scrim,
                isScrimOwner ? null : stylesheet.hiddenScrim,
                scrimDynamicStyle,
                {
                    opacity: scrimOpacityAnim
                }
            ]}
            importantForAccessibility="no-hide-descendants"
            onStartShouldSetResponder={getIsScrimResponder}
            onResponderRelease={onScrimRelease}
            aria-hidden={true}
        />;
    };

    const renderClose = () => {
        if(!isShowClose) {
            return null;
        }

        return <Button
            accessibilityLabel={localize("close")}
            borderRadius={closeStyle.borderRadius}
            customTheme={customTheme}
            spreadBehaviour="free"
            onPress={onClosePress}
            style={[
                stylesheet.close,
                closeDynamicStyle
            ]}
            icon={ModalCloseIcon}
            variant="ghost"
            type="neutral"
            size="small"
        />;
    };

    const renderSurface = () => {
        return <View
            style={[
                stylesheet.gutter,
                gutterDynamicStyle
            ]}
        >
            <View
                style={[
                    stylesheet.surface,
                    surfaceDynamicStyle
                ]}
            >
                <NCoreUIKitTheme.SurfaceProvider
                    surfaceColor={SURFACE_CONTAINER_COLOR}
                >
                    {children}
                </NCoreUIKitTheme.SurfaceProvider>
                {renderClose()}
            </View>
        </View>;
    };

    const renderDialog = () => {
        return <Animated.View
            {...containerProps}
            {...webProps}
            accessibilityLabel={accessibilityLabel}
            aria-labelledby={ariaLabelledBy}
            style={[
                contentStyle,
                stylesheet.dialog,
                dialogDynamicStyle,
                {
                    transform: [
                        {
                            scale: scaleAnim
                        }
                    ]
                }
            ]}
            aria-label={ariaLabel}
            ref={containerRef}
            role={role}
        >
            {isShowSurface ? renderSurface() : children}
        </Animated.View>;
    };

    return <ModalPortalContext.Provider
        value={portalName}
    >
        <Animated.View
            style={[
                style,
                stylesheet.container,
                isOverlayVisible ? stylesheet.passThroughContainer : null,
                {
                    opacity: opacityAnim
                }
            ]}
            onLayout={onContainerLayout}
        >
            {renderScrim()}
            {renderDialog()}
        </Animated.View>
    </ModalPortalContext.Provider>;
};

/**
 * A generic modal
 * @param props {@link IModalProps}
 * @returns Element
 */
const Modal: RefForwardingComponent<IModalRef, IModalProps> = ({
    isScaleAnimated: isScaleAnimatedProp,
    "aria-describedby": ariaDescribedBy,
    "aria-labelledby": ariaLabelledBy,
    isScaleAnimatedOnlyOpen = true,
    isActive: isActiveDeprecated,
    isDisabledOverlay = false,
    "aria-label": ariaLabel,
    alignContent = "center",
    isOverlayVisible = true,
    isWorkWithPortal = true,
    isShowSurface = false,
    closeAnimationDelay,
    accessibilityLabel,
    openAnimationDelay,
    isAnimated = true,
    isContentRequired,
    onContainerLayout,
    initialFocusRef,
    role = "dialog",
    customLocalize,
    onOverlayPress,
    returnFocusRef,
    contentStyle,
    onOpenChange,
    overlayProps,
    customTheme,
    isShowClose,
    portalName,
    id: outID,
    children,
    onClosed,
    isOpen,
    style
}, ref) => {
    const {
        durations
    } = NCoreUIKitTheme.useContext(customTheme);

    const isActiveProp = isOpen ?? isActiveDeprecated;

    const isReduceMotionEnabled = useReducedMotion();

    const parentPortalName = useContext(ModalPortalContext);

    const id = useRef(outID ? outID : uuid());

    const [
        isRendered,
        setIsRendered
    ] = useState(isActiveProp === undefined ? true : isActiveProp);

    const [
        stackPortalName,
        setStackPortalName
    ] = useState(() => isActiveProp === false ? undefined : getStackPortalName());

    const [
        isClosing,
        setIsClosing
    ] = useState(false);

    const isScaleAnimated = isScaleAnimatedProp ?? alignContent === "center";

    const opacityAnim = useRef(new Animated.Value(isAnimated ? 0 : 1)).current;

    const scaleAnim = useRef(new Animated.Value(isAnimated && isScaleAnimated ? MOTION_SURFACE_SCALE : 1)).current;

    const closeCallbacks = useRef<Array<(props: {
        id: string;
    }) => void>>([]);

    const isRenderedRef = useRef(isRendered);

    const isClosingRef = useRef(false);

    const onClosedRef = useRef(onClosed);

    const isNativeDriver = Platform.OS !== "web";

    const currentPortalName = getModalPortalName({
        parentPortalName,
        stackPortalName,
        portalName
    });

    const currentMotion = getModalMotion({
        isReduceMotionEnabled,
        closeAnimationDelay,
        openAnimationDelay,
        durations
    });

    useEffect(() => {
        onClosedRef.current = onClosed;
    });

    useEffect(() => {
        if(isActiveDeprecated !== undefined) {
            warnOnce({
                message: "Modal: \"isActive\" is deprecated and will be removed in the next major version. Use \"isOpen\" instead."
            });
        }
    }, [
        isActiveDeprecated
    ]);

    useEffect(() => {
        if(isContentRequired === undefined) {
            return;
        }

        warnOnce({
            message: "Modal: \"isContentRequired\" currently has no effect. The children are always rendered inside the dialog layer."
        });
    }, [
        isContentRequired
    ]);

    useEffect(() => {
        if(!isRendered || isClosing) {
            return;
        }

        if(!isAnimated) {
            opacityAnim.setValue(1);

            scaleAnim.setValue(1);

            return;
        }

        const animations = [
            Animated.timing(opacityAnim, {
                duration: currentMotion.openFadeDuration,
                easing: currentMotion.openEasing,
                useNativeDriver: isNativeDriver,
                toValue: 1
            })
        ];

        if(isScaleAnimated) {
            animations.push(Animated.timing(scaleAnim, {
                duration: currentMotion.openMoveDuration,
                easing: currentMotion.openEasing,
                useNativeDriver: isNativeDriver,
                toValue: 1
            }));
        }

        Animated.parallel(animations).start();
    }, [
        isRendered,
        isClosing
    ]);

    useEffect(() => {
        return () => {
            opacityAnim.stopAnimation();

            scaleAnim.stopAnimation();
        };
    }, []);

    const updateIsRendered = (nextIsRendered: boolean) => {
        if(nextIsRendered && !isRenderedRef.current) {
            setStackPortalName(getStackPortalName());
        }

        isRenderedRef.current = nextIsRendered;

        setIsRendered(nextIsRendered);
    };

    const flushCloseCallbacks = () => {
        closeCallbacks.current.splice(0).forEach((callback) => {
            callback({
                id: id.current
            });
        });
    };

    const stopClosing = () => {
        if(!isClosingRef.current) {
            return;
        }

        isClosingRef.current = false;

        opacityAnim.stopAnimation();

        scaleAnim.stopAnimation();

        setIsClosing(false);
    };

    const finishClose = () => {
        isClosingRef.current = false;

        setIsClosing(false);

        updateIsRendered(false);

        if(isAnimated && isScaleAnimated) {
            scaleAnim.setValue(MOTION_SURFACE_SCALE);
        }

        flushCloseCallbacks();

        if(onClosedRef.current) onClosedRef.current({
            id: id.current
        });
    };

    const closeAnimation = (_onClosed?: (props: {
        id: string;
    }) => void) => {
        if(!isRenderedRef.current) {
            if(_onClosed) _onClosed({
                id: id.current
            });

            return;
        }

        if(_onClosed) {
            closeCallbacks.current.push(_onClosed);
        }

        if(isClosingRef.current) {
            return;
        }

        isClosingRef.current = true;

        setIsClosing(true);

        if(!isAnimated) {
            finishClose();

            return;
        }

        const animations = [
            Animated.timing(opacityAnim, {
                duration: currentMotion.closeFadeDuration,
                easing: currentMotion.closeEasing,
                useNativeDriver: isNativeDriver,
                toValue: 0
            })
        ];

        if(isScaleAnimated && !isScaleAnimatedOnlyOpen) {
            animations.push(Animated.timing(scaleAnim, {
                duration: currentMotion.closeMoveDuration,
                easing: currentMotion.closeEasing,
                useNativeDriver: isNativeDriver,
                toValue: MOTION_SURFACE_SCALE
            }));
        }

        Animated.parallel(animations).start(({
            finished
        }) => {
            if(!finished) {
                return;
            }

            finishClose();
        });
    };

    const open = () => {
        stopClosing();

        updateIsRendered(true);
    };

    const setIsActive = (newActiveState: boolean) => {
        if(newActiveState) {
            open();

            return;
        }

        stopClosing();

        updateIsRendered(false);

        flushCloseCallbacks();
    };

    useEffect(() => {
        if(isActiveProp === undefined) {
            return;
        }

        if(isActiveProp) {
            open();

            return;
        }

        closeAnimation();
    }, [
        isActiveProp
    ]);

    useImperativeHandle(
        ref,
        () => ({
            closeAnimation,
            setIsActive
        })
    );

    const onDismiss = () => {
        if(isDisabledOverlay || isClosingRef.current || (!onOverlayPress && !onOpenChange)) {
            return;
        }

        if(onOpenChange) onOpenChange(false);

        if(onOverlayPress) onOverlayPress({
            closeAnimation
        });
    };

    const onClosePress = () => {
        if(onOpenChange) onOpenChange(false);

        closeAnimation();
    };

    const renderLayer = () => {
        return <ModalLayer
            portalName={isWorkWithPortal ? currentPortalName : parentPortalName}
            onDismiss={onOverlayPress || onOpenChange ? onDismiss : undefined}
            isShowClose={isShowSurface && (isShowClose ?? true)}
            accessibilityLabel={accessibilityLabel}
            onContainerLayout={onContainerLayout}
            isDisabledOverlay={isDisabledOverlay}
            isOverlayVisible={isOverlayVisible}
            aria-describedby={ariaDescribedBy}
            initialFocusRef={initialFocusRef}
            aria-labelledby={ariaLabelledBy}
            returnFocusRef={returnFocusRef}
            customLocalize={customLocalize}
            isShowSurface={isShowSurface}
            onClosePress={onClosePress}
            alignContent={alignContent}
            contentStyle={contentStyle}
            overlayProps={overlayProps}
            customTheme={customTheme}
            opacityAnim={opacityAnim}
            motion={currentMotion}
            aria-label={ariaLabel}
            isActive={!isClosing}
            scaleAnim={scaleAnim}
            style={style}
            role={role}
        >
            {children}
        </ModalLayer>;
    };

    if(!isRendered) {
        return null;
    }

    if(!isWorkWithPortal) {
        return renderLayer();
    }

    return <Portal
        name={currentPortalName}
    >
        {renderLayer()}
    </Portal>;
};
export default forwardRef(Modal);
